/**
 * @fileoverview Load secondary bonus evidence only when opened, against the exact displayed report. Responses from another year or snapshot are discarded; successful hydration preserves grid position and editor state. Bonuses owns report refresh and navigation. Financial commands still validate live inputs on the server.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../../../api';

export default function useBonusReportDetails(data, setData, view, selected, editor, reload) {
    const [errors, setErrors] = useState({});
    const [attempt, setAttempt] = useState(0);
    const pending = useRef(new Set());
    const reloadRef = useRef(reload);
    reloadRef.current = reload;
    const snapshotId = data?.report_state?.id;
    const currentSnapshot = useRef(snapshotId);
    currentSnapshot.current = snapshotId;
    const requirements = [];
    if (snapshotId && data.details_pending) {
        if (view === 'History' || view === 'Sources') requirements.push({ key: view.toLowerCase(), section: view.toLowerCase(), owner: 'view' });
        if (view !== 'History' && selected?.kind === 'receipt' && selected.section === 'history' && (selected.year == null || selected.year === data.year)) requirements.push({ key: 'history', section: 'history', owner: 'inspector' });
        if (selected?.kind === 'receipt' && (selected.year == null || selected.year === data.year)) requirements.push({ key: `receipt:${selected.receiptId}`, section: 'receipt', receipt_id: selected.receiptId, owner: 'inspector' });
        if (selected?.kind === 'person' && (selected.year == null || selected.year === data.year)) requirements.push({ key: `person:${selected.employeeId}`, section: 'person', employee_id: selected.employeeId, owner: 'inspector' });
        if (['payment', 'project', 'link_workbook'].includes(editor?.command.action)) requirements.push({ key: 'catalog', section: 'catalog', owner: 'editor' });
    }
    const requirementKey = requirements.map(r => r.key).join('|');
    useEffect(() => {
        for (const { key, owner, ...params } of requirements) {
            const requestKey = `${snapshotId}:${key}`;
            if (data._details?.includes(key) || pending.current.has(requestKey)) continue;
            pending.current.add(requestKey);
            api.get('/admin/bonuses/details', { params: { year: data.year, snapshot_id: snapshotId, ...params } }).then(response => {
                setData(current => {
                    if (current?.report_state?.id !== snapshotId) return current;
                    const result = response.data;
                    const next = { ...current, _details: [...(current._details || []), key] };
                    if (result.row) next.rows = current.rows.map(row => row.id === result.row.id ? result.row : row);
                    else if (result.person) {
                        next.people = current.people.map(p => p.id === result.person.id ? result.person : p);
                        next.all_people = current.all_people.map(p => p.id === result.person.id ? result.person : p);
                    } else Object.assign(next, result);
                    return next;
                });
                setErrors(current => ({ ...current, [requestKey]: null }));
            }).catch(error => {
                if (currentSnapshot.current !== snapshotId) return;
                if (error.response?.status === 409) reloadRef.current();
                else setErrors(current => ({ ...current, [requestKey]: error.response?.data?.error || 'Bonus details could not be loaded.' }));
            }).finally(() => pending.current.delete(requestKey));
        }
    }, [snapshotId, requirementKey, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
    const missing = requirements.filter(item => !data._details?.includes(item.key));
    return {
        viewLoading: missing.some(r => r.owner === 'view'),
        inspectorLoading: missing.some(r => r.owner === 'inspector'),
        editorLoading: missing.some(r => r.owner === 'editor'),
        error: missing.map(r => errors[`${snapshotId}:${r.key}`]).find(Boolean),
        retry: () => setAttempt(value => value + 1),
    };
}
